import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { InfiniteMovingCards } from "@/components/InfiniteMovingCards";
import { Hero } from "@/components/home/Hero";
import { FeaturesSection } from "@/components/home/FeaturesSection";
import { PricingSection } from "@/components/home/PricingSection";
import { DailyMoodCheckIn } from "@/components/DailyMoodCheckIn";
import { useEffect, useState } from "react";
import { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import SocialButtons from "@/components/home/SocialButtons";
import { TracingBeam } from "@/components/ui/tracing-beam";
import { Brain, Heart, Star } from "lucide-react";
import { Card } from "@/components/ui/card";

const testimonials = [
  {
    quote: "DBTAI has transformed my approach to emotional regulation. The AI-powered chat support feels like having a DBT therapist available 24/7.",
    name: "Sarah M.",
    title: "DBT Practitioner",
  },
  {
    quote: "The safety planning feature provides incredible peace of mind. Being able to update and share it with my support network has been invaluable.",
    name: "James R.",
    title: "Mental Health Advocate",
  },
  {
    quote: "The structured DBT journaling has helped me identify emotional patterns and triggers I never noticed before. It's been transformative.",
    name: "Emily K.",
    title: "Long-term DBT Practitioner",
  },
  {
    quote: "As a therapist, I recommend DBTAI to my clients. It bridges the gap between sessions with evidence-based DBT tools and real-time support.",
    name: "Dr. Michael P.",
    title: "Licensed Clinical Psychologist",
  },
];

const evidencePoints = [
  {
    icon: Brain,
    title: "Scientifically Validated",
    description: "DBT has been extensively researched and proven effective for emotional regulation, mindfulness, and interpersonal effectiveness.",
  },
  {
    icon: Heart,
    title: "Comprehensive Support",
    description: "Our platform combines traditional DBT techniques with AI technology to provide 24/7 accessible mental health support.",
  },
  {
    icon: Star,
    title: "Measurable Results",
    description: "Users report significant improvements in emotional regulation and decreased distress levels within weeks of consistent practice.",
  },
];

const Index = () => {
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <div className="min-h-screen w-full overflow-hidden">
      <TracingBeam className="w-full px-4 md:px-8">
        <div className="relative w-full">
          <DailyMoodCheckIn session={session} />
          <Hero />
          <FeaturesSection />

          {/* Evidence-Based Approach Section */}
          <section className="py-12 md:py-16 bg-secondary/5">
            <div className="container mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">Evidence-Based Approach</h2>
              <p className="text-lg text-foreground/80 text-center max-w-3xl mx-auto mb-12">
                Our platform is built on scientifically validated DBT principles, enhanced with modern technology to provide accessible and effective support.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {evidencePoints.map((point, index) => (
                  <Card key={index} className="p-6 backdrop-blur-lg border border-secondary/20 card-hover">
                    <div className="flex flex-col items-center text-center">
                      <div className="p-3 rounded-full bg-secondary/10 mb-4">
                        <point.icon className="h-6 w-6 text-secondary" />
                      </div>
                      <h3 className="text-xl font-semibold mb-2">{point.title}</h3>
                      <p className="text-foreground/80">{point.description}</p>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          <PricingSection />

          <section className="py-12 md:py-16 bg-primary/5">
            <div className="container mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">Community Success Stories</h2>
              <div className="max-w-full">
                <InfiniteMovingCards items={testimonials} />
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-secondary/10 to-accent/10 animate-pulse" />
            <div className="container mx-auto text-center relative z-10">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">Begin Your Journey Today</h2>
              <p className="text-lg text-foreground/80 max-w-2xl mx-auto mb-8">
                Join thousands of others who have found support and growth through our comprehensive DBT tools.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button className="transform hover:scale-105 transition-transform" asChild>
                  <Link to="/chat">Start Free Trial</Link>
                </Button>
                <Button variant="secondary" className="transform hover:scale-105 transition-transform" asChild>
                  <Link to="/safety-plan">Create Safety Plan</Link>
                </Button>
              </div>
            </div>
          </section>

          <SocialButtons />
        </div>
      </TracingBeam>
    </div>
  );
};

export default Index;